'use client';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { ButtonTab } from '@/app/components/shared/button-tab';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import { cardItemVariants, cardsContainerVariants } from '@/app/components/shared/motion-variants';
import { EditionSwitcher, type PlanEdition } from '@/app/components/shared/PlanDetail/components/EditionSwitcher';
import { ErrorComponent } from '@/app/components/shared/PlanDetail/components/Error';
import { ProductEmptyState } from '@/app/components/shared/ProductEmptyState';
import type {
  SubscriptionProductType,
  TeamPeriodType,
  TeamProductList,
  TeamProductPlan,
  TeamTabItemType,
} from '@/app/types';
import { cn } from '@/app/utils';
import { ConsultButton } from './components/ConsultButton';
import { TeamPlanCard } from './components/TeamPlanCard';
import { TeamSubscriptionButton } from './components/TeamSubscriptionButton';

interface TeamPlanDetailProps {
  loadingProducts: boolean;
  products: TeamProductList | null;
  tabList: TeamTabItemType[];
  /** 接口返回的当前订阅所在 period；外部不传则 fallback 到 tabList[0]。 */
  initialPeriod: TeamPeriodType | null;
  error?: Error | null;
  /** 仅查看不可购买（企业空间下的成员用户） */
  purchaseDisabled?: boolean;
  className?: string;
  editionOptions?: { key: PlanEdition; label: string }[];
  edition?: PlanEdition;
  onEditionChange?: (key: PlanEdition) => void;
}

/**
 * 企业版套餐列表：
 * - 数据走 useFetchProductList.buildTeamProductMap，结构与个人版 ProductList 对齐
 *   （`Record<TeamPeriodType, Record<TeamTier, SubscriptionProductType | null>>`）；
 * - 顶部 Tab 切换 年付 / 季付，视觉与个人版 PlanDetail Tab 一致；
 * - 卡片按 basic → plus → pro 顺序渲染，末尾追加"更高版本"咨询卡，共 4 张。
 */
export const TeamPlanDetail = ({
  loadingProducts,
  products,
  tabList,
  initialPeriod,
  error,
  purchaseDisabled = false,
  className,
  editionOptions,
  edition,
  onEditionChange,
}: TeamPlanDetailProps) => {
  const [internalPeriod, setInternalPeriod] = useState<TeamPeriodType | null>(null);
  const period = internalPeriod ?? initialPeriod;

  const renderSubscriptionButton = (product: SubscriptionProductType) => (
    <TeamSubscriptionButton loading={false} product={product} purchaseDisabled={purchaseDisabled} />
  );

  if (error) return <ErrorComponent />;
  if (loadingProducts && !products)
    return (
      <div className="flex min-h-[400px] w-full items-center justify-center">
        <GlobalLoader className="static translate-x-0 translate-y-0" />
      </div>
    );
  if (!products || !period || tabList.length === 0) return <ProductEmptyState className="mt-10" />;

  const tierKeys: Array<keyof TeamProductPlan> = ['basic', 'plus', 'pro'];
  const isDiscount = tabList.find((v) => v.value === period)?.isDiscount;

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="relative flex items-center sm:flex-col sm:items-stretch">
        <ButtonTab
          className="relative my-6 flex w-fit justify-center rounded-12 border border-system-white08 bg-system-white08 p-0 font-normal sm:mx-4 sm:my-4 sm:w-auto"
          buttonClassName="relative h-auto min-w-[160px] rounded-12 border border-transparent px-10 py-3 text-sm text-system-white opacity-[0.64] gap-2 overflow-hidden aria-selected:opacity-100 aria-selected:bg-system-white24 aria-selected:border-system-white08 sm:w-full sm:min-w-0 sm:flex-wrap sm:min-h-[44px] sm:px-3"
          active={period}
          onChange={(val: TeamPeriodType) => setInternalPeriod(val)}
          options={tabList.map((item) => ({ value: item.value, label: item.label }))}
        />
        <EditionSwitcher
          editionOptions={editionOptions}
          edition={edition}
          onEditionChange={onEditionChange}
          className="-translate-y-1/2 absolute top-1/2 right-0 sm:static sm:mr-4 sm:ml-auto sm:w-fit sm:translate-y-0"
        />
      </div>

      <motion.div
        key={period}
        className="grid grid-cols-4 gap-4 sm:grid-cols-1 sm:px-4 md:grid-cols-2 lg:grid-cols-2"
        initial="hidden"
        animate="visible"
        variants={cardsContainerVariants}
      >
        {tierKeys.map((tier) => {
          const product = products[period]?.[tier];
          if (!product) return null;
          return (
            <motion.div key={`${tier}_${period}`} variants={cardItemVariants}>
              <TeamPlanCard
                tier={tier}
                product={product}
                isDiscount={isDiscount}
                renderSubscriptionButton={renderSubscriptionButton}
              />
            </motion.div>
          );
        })}
        <motion.div variants={cardItemVariants}>
          <TeamPlanCard variant="consult" renderSubscriptionButton={() => <ConsultButton />} />
        </motion.div>
      </motion.div>
    </div>
  );
};
